/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { toast } from 'react-toastify';

import FileSaver from 'file-saver';
import { all, takeLatest, call, put } from 'redux-saga/effects';
import * as XLSX from 'xlsx';

import api from '~/services/api';
import history from '~/services/history';

import {
  getDataListMetadataSuccess,
  getDataListMetadataFailure,
  listDataListSuccess,
  listDataListFailure,
  activateDataListSuccess,
  activateDataListFailure,
  saveDataListSuccess,
  saveDataListFailure,
  getDataListSuccess,
  getDataListFailure,
  getMetadataTemplateDataListSuccess,
  getMetadataTemplateDataListFailure,
  getMetadataFileDataListSuccess,
  getMetadataFileDataListFailure,
} from './actions';
import {
  IActivateDataListRequestAction,
  IDataListTypes,
  IGetDataListMetadataRequestAction,
  IGetDataListRequestAction,
  IGetMetadataFileByNameDataListRequestAction,
  IGetMetadataFileDataListRequestAction,
  IListDataListRequestAction,
  ISaveDataListRequestAction,
} from './types';

function* listDataList({ payload }: IListDataListRequestAction): any {
  const { page, sortOrder, sortBy, limit, search } = payload;

  try {
    const response = yield call(api.get, '/data-lists', {
      params: { page, sortOrder, sortBy, limit, search },
    });

    const { data, total } = response.data;

    yield put(listDataListSuccess(data, total, sortOrder, sortBy));
  } catch (_err) {
    yield put(listDataListFailure());
    toast.error('Erro de conexão, tente mais tarde.');
  }
}

function* getDataList({ payload }: IGetDataListRequestAction): any {
  const { id } = payload;

  try {
    const response = yield call(api.get, `/data-lists/${id}`);

    const dataList = response.data;

    yield put(getDataListSuccess(dataList));
  } catch (_err) {
    yield put(getDataListFailure());
    history.push('/data-lists');
    toast.error('Houve um problema ao buscar os dados.');
  }
}

function* getDataListMetadata({
  payload,
}: IGetDataListMetadataRequestAction): any {
  const { formFieldId, parentFormFieldValue, search, page, limit } = payload;

  try {
    const response = yield call(
      api.get,
      `/data-lists/${formFieldId}/metadata`,
      {
        params: {
          page,
          limit,
          metadataValue: search,
          parentMetadataValue: parentFormFieldValue ?? '',
        },
      },
    );

    const { total, data } = response.data;

    yield put(getDataListMetadataSuccess(total, data));
  } catch (_err) {
    yield put(getDataListMetadataFailure());
    toast.error('Houve um problema ao buscar os dados.');
  }
}

function* activateDataList({ payload }: IActivateDataListRequestAction) {
  const { data_list_id, active } = payload;

  try {
    yield call(api.post, `/data-lists/${data_list_id}/activate`, {
      active,
    });

    yield put(activateDataListSuccess(data_list_id, active));
  } catch (_err) {
    yield put(activateDataListFailure());
    toast.error('Falha ao ativar/inativar cadastro.');
  }
}

function* saveDataList({ payload }: ISaveDataListRequestAction): any {
  const {
    id,
    name,
    form_field_id,
    form_field_label,
    active,
    parent_id,
    responsible,
  } = payload;

  try {
    if (id) {
      yield call(api.put, `/data-lists/${id}`, {
        name,
        form_field_id,
        form_field_label,
        active,
        parent_id,
        responsible,
      });
    } else {
      yield call(api.post, '/data-lists', {
        id,
        name,
        form_field_id,
        form_field_label,
        active,
        parent_id,
        responsible,
      });
    }
    yield put(saveDataListSuccess());
    history.push('/data-lists');
    toast.success('Cadastro salvo com sucesso.');
  } catch (err) {
    yield put(saveDataListFailure());
    toast.error('Erro ao salvar cadastro');
  }
}

function* getMetadataTemplate(): any {
  try {
    const { data } = yield call(api.get, '/data-lists/metadata-template');
    window.open(data);
    yield put(getMetadataTemplateDataListSuccess());
  } catch (err) {
    yield put(getMetadataTemplateDataListFailure());
    toast.error('Erro ao baixar o template do arquivo');
  }
}

function* getMetadataFileByName({
  payload,
}: IGetMetadataFileByNameDataListRequestAction): any {
  const { filePath } = payload;
  try {
    const { data } = yield call(api.get, '/data-lists/metadata-file', {
      params: { filePath },
    });
    window.open(data);
    yield put(getMetadataTemplateDataListSuccess());
  } catch (err) {
    yield put(getMetadataTemplateDataListFailure());
    toast.error('Erro ao baixar o template do arquivo');
  }
}

function* getMetadataFile({
  payload,
}: IGetMetadataFileDataListRequestAction): any {
  const { data_list_id } = payload;
  try {
    const response = yield call(
      api.get,
      `/data-lists/${data_list_id}/metadata-file`,
    );

    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

    const ws = XLSX.utils.json_to_sheet(response.data);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });

    FileSaver.saveAs(data, 'dados.xlsx');
    yield put(getMetadataFileDataListSuccess());
  } catch (err) {
    yield put(getMetadataFileDataListFailure());
    toast.error('Erro ao baixar o template do arquivo');
  }
}

export default all([
  takeLatest(IDataListTypes.LIST_DATA_LIST_REQUEST, listDataList),
  takeLatest(IDataListTypes.GET_DATA_LIST_REQUEST, getDataList),
  takeLatest(
    IDataListTypes.GET_DATA_LIST_METADATA_REQUEST,
    getDataListMetadata,
  ),
  takeLatest(IDataListTypes.ACTIVATE_DATA_LIST_REQUEST, activateDataList),
  takeLatest(IDataListTypes.SAVE_DATA_LIST_REQUEST, saveDataList),
  takeLatest(
    IDataListTypes.GET_METADATA_TEMPLATE_DATA_LIST_REQUEST,
    getMetadataTemplate,
  ),
  takeLatest(
    IDataListTypes.GET_METADATA_FILE_DATA_LIST_REQUEST,
    getMetadataFile,
  ),
  takeLatest(
    IDataListTypes.GET_METADATA_FILE_BY_NAME_DATA_LIST_REQUEST,
    getMetadataFileByName,
  ),
]);
